
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

export default function Header() {
  const { t, i18n } = useTranslation('common');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Páginas com hero de fundo CLARO (texto escuro quando não scrollado)
  const lightHeroPages = ['/'];
  const isLightHero = lightHeroPages.includes(location.pathname);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'pt' ? 'en' : 'pt';
    i18n.changeLanguage(newLang);
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Cor base dos links quando não scrollado
  const baseTextColor = isScrolled
    ? 'text-[#2E2E2E]'
    : isLightHero
    ? 'text-[#2E2E2E]'
    : 'text-white/90';

  const navLinkClass = (path: string) => {
    const active = isActive(path);
    const activeColor = active ? 'text-[#F97700] font-semibold' : baseTextColor;
    return `text-sm ${activeColor} hover:text-[#F97700] transition-colors cursor-pointer whitespace-nowrap`;
  };

  // Cor do logo
  const logoColor = isScrolled
    ? 'text-[#0B0B0B]'
    : isLightHero
    ? 'text-[#0B0B0B]'
    : 'text-white';

  // Cor do botão de idioma
  const langColor = isScrolled
    ? 'text-[#2E2E2E] hover:text-[#F97700]'
    : isLightHero
    ? 'text-[#2E2E2E] hover:text-[#F97700]'
    : 'text-white/80 hover:text-white';

  // Cor das barras do hambúrguer
  const hamburgerColor = isScrolled
    ? 'bg-[#0B0B0B]'
    : isLightHero
    ? 'bg-[#0B0B0B]'
    : 'bg-white';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="px-6 py-5">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-8">

          {/* Logo — vinculado à homepage */}
          <a href="/" className="flex items-center cursor-pointer shrink-0">
            <span className={`text-xl font-semibold tracking-tight transition-colors ${logoColor}`}>
              FGT Capital
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            <a href="/solucoes" className={navLinkClass('/solucoes')}>
              {t('nav.solutions')}
            </a>
            <a href="/metodo" className={navLinkClass('/metodo')}>
              {t('nav.method')}
            </a>
            <a href="/cartas-contempladas" className={navLinkClass('/cartas-contempladas')}>
              {t('nav.contemplatedCards')}
            </a>
            <a href="/empresas" className={navLinkClass('/empresas')}>
              {t('nav.companies')}
            </a>
            <a href="/conteudos" className={navLinkClass('/conteudos')}>
              {t('nav.contents')}
            </a>
            <a href="/sobre" className={navLinkClass('/sobre')}>
              {t('nav.about')}
            </a>
            <a href="/contato" className={navLinkClass('/contato')}>
              {t('nav.contact')}
            </a>
          </nav>

          {/* CTA & Language */}
          <div className="hidden lg:flex items-center gap-4 shrink-0">
            <button
              onClick={toggleLanguage}
              className={`text-xs font-medium transition-colors uppercase tracking-wider cursor-pointer whitespace-nowrap ${langColor}`}
            >
              {i18n.language === 'pt' ? 'EN' : 'PT'}
            </button>
            <a
              href="/diagnostico"
              className="bg-[#F97700] text-white px-5 py-2.5 text-xs font-medium tracking-wide hover:bg-[#E56E00] transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('cta.diagnosis')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-6 h-6 flex flex-col justify-center items-center gap-1.5 cursor-pointer"
          >
            <span className={`w-5 h-0.5 transition-all ${hamburgerColor}`}></span>
            <span className={`w-5 h-0.5 transition-all ${hamburgerColor}`}></span>
            <span className={`w-5 h-0.5 transition-all ${hamburgerColor}`}></span>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-6 pb-6 border-t border-[#E7E1D9] pt-6 bg-white/98">
            <nav className="flex flex-col space-y-4">
              <a href="/solucoes" className="text-sm text-[#2E2E2E] hover:text-[#F97700] cursor-pointer">
                {t('nav.solutions')}
              </a>
              <a href="/metodo" className="text-sm text-[#2E2E2E] hover:text-[#F97700] cursor-pointer">
                {t('nav.method')}
              </a>
              <a href="/cartas-contempladas" className="text-sm text-[#2E2E2E] hover:text-[#F97700] cursor-pointer">
                {t('nav.contemplatedCards')}
              </a>
              <a href="/empresas" className="text-sm text-[#2E2E2E] hover:text-[#F97700] cursor-pointer">
                {t('nav.companies')}
              </a>
              <a href="/conteudos" className="text-sm text-[#2E2E2E] hover:text-[#F97700] cursor-pointer">
                {t('nav.contents')}
              </a>
              <a href="/sobre" className="text-sm text-[#2E2E2E] hover:text-[#F97700] cursor-pointer">
                {t('nav.about')}
              </a>
              <a href="/contato" className="text-sm text-[#2E2E2E] hover:text-[#F97700] cursor-pointer">
                {t('nav.contact')}
              </a>
              <button
                onClick={toggleLanguage}
                className="text-xs font-medium text-[#2E2E2E] hover:text-[#F97700] uppercase tracking-wider text-left cursor-pointer"
              >
                {i18n.language === 'pt' ? 'EN' : 'PT'}
              </button>
              <a
                href="/diagnostico"
                className="bg-[#F97700] text-white px-6 py-3 text-xs font-medium tracking-wide hover:bg-[#E56E00] transition-colors text-center cursor-pointer"
              >
                {t('cta.diagnosis')}
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
